import {
  Box,
  Button,
  MenuItem,
  Paper,
  TextField,
} from "@mui/material";
import { useState, useEffect } from "react";

import API_Calls_Utilities from "@/api/calls/API_Calls_Utilities";

const LIMIT_OPTIONS = [
  { value: "", label: "Any" },
  { value: "10", label: "10" },
  { value: "30", label: "30" },
  { value: "100", label: "100" },
  { value: "300", label: "300" },
  { value: "1000", label: "1000" },
];

const LEVEL_OPTIONS = [
  { value: "", label: "Any" },
  { value: "info", label: "Info" },
  { value: "debug", label: "Debug" },
  { value: "warn", label: "Warn" },
  { value: "error", label: "Error" },
  { value: "notify", label: "Notify" },
  { value: "fatal", label: "Fatal" },
];

const TAG_OPTIONS = [
  { value: "", label: "Any" },
  { value: "api-event", label: "api-event" },
  { value: "api-membership", label: "api-membership" },
  { value: "backup", label: "backup" },
  { value: "cache", label: "cache" },
  { value: "canadian", label: "canadian" },
  { value: "catalist", label: "catalist" },
  { value: "clean", label: "clean" },
  { value: "dashboard", label: "dashboard" },
];

const INITIAL_FORM_DATA = {
  startDate: "",
  limit: "1000",
  level: "",
  tag: "",
  message: "",
  objectName: "",
};

function LogsDisplay() {
  const [logs, setLogs] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const { getLogs } = API_Calls_Utilities();

  useEffect(() => {
    const refresh = true;
    fetchLogs(refresh);
    // fetchLogs/getLogs are recreated each render and would cause
    // infinite re-runs; intentionally fire only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLogs = async (refresh) => {
    const params = buildParams(refresh);
    const res = await getLogs(params);
    if (!res.error) {
      setLogs(res.data);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const buildParams = (refresh) => {
    const params = {};
    // only append fields if they have a value
    if (formData.startDate) {
      const formattedDate = formData.startDate.replace("T", " ") + ":00";
      params["start_date"] = formattedDate;
    }
    if (formData.limit) params["limit"] = formData.limit;
    if (formData.level) params["level"] = formData.level;
    if (formData.tag) params["tag"] = formData.tag;
    if (formData.message) params["message"] = formData.message;
    if (formData.objectName) params["object_name"] = formData.objectName;
    params["refresh"] = refresh;
    return params;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const refresh = false;
    fetchLogs(refresh);
  };

  const handleClear = () => {
    setFormData(INITIAL_FORM_DATA);
  };

  const handleRefresh = () => {
    const refresh = true;
    fetchLogs(refresh);
  };

  return (
    <Box>
      <Box>
        <div dangerouslySetInnerHTML={{ __html: logs }} />
      </Box>
      <Paper
        variant="outlined"
        sx={{
          position: "fixed",
          bottom: "10px",
          left: "10px",
          right: "10px",
          zIndex: 1,
          px: "10px",
          pt: "15px",
          pb: "10px",
          borderRadius: 1,
          backgroundColor: "#FBF8FE",
          borderWidth: 2,
          borderColor: "#664697",
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <TextField
              id="startDate"
              name="startDate"
              label="Start date"
              type="datetime-local"
              size="small"
              value={formData.startDate}
              onChange={handleChange}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 220 }}
            />
            <TextField
              select
              id="limit"
              name="limit"
              label="Row limit"
              size="small"
              value={formData.limit}
              onChange={handleChange}
              sx={{ minWidth: 130 }}
            >
              {LIMIT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "any"} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              id="level"
              name="level"
              label="Level"
              size="small"
              value={formData.level}
              onChange={handleChange}
              sx={{ minWidth: 140 }}
            >
              {LEVEL_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "any"} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              id="tag"
              name="tag"
              label="Tag"
              size="small"
              value={formData.tag}
              onChange={handleChange}
              sx={{ minWidth: 170 }}
            >
              {TAG_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "any"} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                ml: { xs: 0, sm: "auto" },
              }}
            >
              <Button type="submit" variant="contained" size="small">
                Apply filters
              </Button>
              <Button
                type="button"
                onClick={handleClear}
                size="small"
                sx={{ marginRight: 0 }}
              >
                Clear
              </Button>
              <Button
                type="button"
                onClick={handleRefresh}
                size="small"
                sx={{ marginRight: 0 }}
              >
                Reload logs
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <TextField
              id="message"
              name="message"
              label="Message contains"
              size="small"
              value={formData.message}
              onChange={handleChange}
              placeholder="case-insensitive substring"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              id="objectName"
              name="objectName"
              label="Object name contains"
              size="small"
              value={formData.objectName}
              onChange={handleChange}
              placeholder="case-insensitive substring"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 210 }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default LogsDisplay;
